import React from 'react';
import PropTypes from 'prop-types';
import { InlineValidationType } from '@ids/inline-validation-message';
import { themes } from '@ids/theme';
import { styleOverrideReason } from '@ids/core';
import {
  CheckboxInput,
  RadioInput,
  Container,
  InnerCheckboxSpan,
  InnerRadioSpan
} from './StyledRCCheckbox';

const { string, bool, number, func, oneOfType, oneOf } = PropTypes;

/**
 *
 */
export default class Checkbox extends React.PureComponent {
  static propTypes = {
    name: string,
    type: string,
    theme: oneOf(themes),
    defaultChecked: oneOfType([number, bool]),
    checked: oneOfType([number, bool]),
    // eslint-disable-next-line react/boolean-prop-naming
    disabled: bool,
    onFocus: func,
    onBlur: func,
    onChange: func,
    onClick: func,
    tabIndex: string,
    // eslint-disable-next-line react/boolean-prop-naming
    readOnly: bool,
    // eslint-disable-next-line react/boolean-prop-naming
    a11yFocus: bool,
    // eslint-disable-next-line react/boolean-prop-naming
    indeterminate: bool,
    validationType: InlineValidationType,
    validationMessage: string,
    assignInputRef: func,
    // eslint-disable-next-line react/require-default-props
    'aria-describedby': string,
    // eslint-disable-next-line react/require-default-props
    'aria-label': string,
    /**
     * When  `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    name: undefined,
    type: 'checkbox',
    defaultChecked: undefined,
    checked: undefined,
    disabled: false,
    onFocus() {},
    onBlur() {},
    onChange() {},
    onClick() {},
    assignInputRef() {},
    tabIndex: undefined,
    theme: 'intuit',
    readOnly: false,
    a11yFocus: false,
    indeterminate: false,
    validationMessage: '',
    validationType: '',
    styleOverrideReason: undefined
  };

  constructor(props) {
    super(props);

    const checked =
      // eslint-disable-next-line no-negated-condition
      typeof props.checked !== 'undefined'
        ? props.checked
        : props.defaultChecked;

    this.state = {
      checked
    };
  }

  static getDerivedStateFromProps(props) {
    if (typeof props.checked !== 'undefined') {
      return { checked: props.checked };
    }

    return null;
  }

  handleChange = e => {
    if (this.props.disabled) {
      return;
    }

    if (typeof this.props.checked === 'undefined') {
      this.setState({
        checked: e.target.checked
      });
    }

    this.props.onChange({
      target: {
        ...this.props,
        checked: e.target.checked
      },
      stopPropagation() {
        e.stopPropagation();
      },
      preventDefault() {
        e.preventDefault();
      }
    });
  };

  getInputRef = c => {
    const { assignInputRef } = this.props;
    this.inputRef = c;
    assignInputRef(c);
  };

  render() {
    const {
      name,
      type,
      disabled,
      readOnly,
      tabIndex,
      theme,
      onClick,
      onFocus,
      onBlur,
      a11yFocus,
      assignInputRef,
      indeterminate,
      validationType,
      validationMessage,
      'aria-describedby': ariaDescribedBy,
      ...others
    } = this.props;

    const { 'aria-label': ariaLabel } = others;
    const ariaId = ariaLabel || ariaDescribedBy || undefined;

    const globalProps = Object.keys(others).reduce((prev, key) => {
      if (
        key.substr(0, 5) === 'aria-' ||
        key.substr(0, 5) === 'data-' ||
        key === 'role'
      ) {
        // eslint-disable-next-line no-param-reassign
        prev[key] = others[key];
      }

      return prev;
    }, {});

    const filterClassNameStyleAndReason = Object.keys(others).reduce(
      (prev, key) => {
        if (key === 'className' || key === 'style') {
          // eslint-disable-next-line no-param-reassign
          prev[key] = others[key];
        }

        return prev;
      },
      {}
    );

    const { checked } = this.state;

    return (
      <Container disabled={disabled} theme={theme}>
        {/* Loading Checkbox or Radio based on the type */}
        {type === 'checkbox' ? (
          <>
            <CheckboxInput
              ref={this.getInputRef}
              name={name}
              theme={theme}
              readOnly={readOnly}
              disabled={disabled}
              tabIndex={tabIndex}
              type={type}
              aria-label={ariaId}
              checked={!!checked}
              onClick={onClick}
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={this.handleChange}
              {...globalProps}
              {...filterClassNameStyleAndReason}
            />
            <InnerCheckboxSpan
              checked={checked}
              disabled={disabled}
              indeterminate={indeterminate}
              theme={theme}
              validationType={validationType}
              validationMessage={validationMessage}
              a11yFocus={a11yFocus}
              {...globalProps}
            />
          </>
        ) : (
          <>
            <RadioInput
              ref={this.getInputRef}
              name={name}
              type={type}
              theme={theme}
              readOnly={readOnly}
              disabled={disabled}
              tabIndex={tabIndex}
              checked={!!checked}
              aria-label={ariaId}
              onClick={onClick}
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={this.handleChange}
              {...globalProps}
              {...filterClassNameStyleAndReason}
            />
            <InnerRadioSpan
              checked={checked}
              disabled={disabled}
              indeterminate={indeterminate}
              theme={theme}
              validationType={validationType}
              validationMessage={validationMessage}
              a11yFocus={a11yFocus}
              {...globalProps}
            />
          </>
        )}
      </Container>
    );
  }
}
